// 3rd Party
import { FileRejection } from 'react-dropzone';
import React, { useMemo } from 'react';

// Utils
import { generateBSONId } from '@/utils/random';

// Constants
import { MAX_FILE_SIZE_BYTES, MAX_FILES, ACCEPTED_FILE_TYPES } from '../../constants/file-upload-config';

// Components
import UploadSection, { FileUploadProps } from '@/views/components/upload-section/UploadSection';
import { FormattedMessageV2 } from '@/views/components/i18n';
import UploadStatus from '../UploadStatus';
import FileDisplay from '../FileDisplay';

type Props = {
  onDrop: FileUploadProps['onDrop'];
  onRemove: (file: File) => void;
  uploadedFiles: File[];
  fileRejections?: FileRejection[];
  isProcessingError: boolean;
};

const UploadStep: React.FC<Props> = ({ onDrop, onRemove, uploadedFiles, fileRejections, isProcessingError }) => {
  const i18nBasePath = 'Modal.PortfolioAIStatementReader.UploadStep';

  // Status
  const hasFiles = !!uploadedFiles.length;
  const hasRejections = !!fileRejections?.length;
  const maxFilesReached = uploadedFiles.length >= MAX_FILES && !hasRejections;
  const showFailureStatus = hasRejections || isProcessingError;

  // Generate stable unique IDs for files
  const filesWithIds = useMemo(() => uploadedFiles.map(file => ({ file, id: generateBSONId() })), [uploadedFiles]);
  const rejectionsWithIds = useMemo(() => fileRejections?.map(rejection => ({ rejection, id: generateBSONId() })) || [], [fileRejections]);

  return (
    <div className="ai-statement-reader-upload-step">
      {!showFailureStatus &&
        (!maxFilesReached ? (
          <UploadSection onDrop={onDrop} accept={ACCEPTED_FILE_TYPES} maxSize={MAX_FILE_SIZE_BYTES} maxFiles={MAX_FILES} />
        ) : (
          <UploadStatus success i18nBasePath={i18nBasePath} />
        ))}

      {showFailureStatus && <UploadStatus success={false} i18nBasePath={i18nBasePath} />}

      <h6 className="uploaded-file-title">
        <FormattedMessageV2 toLocalize={`${i18nBasePath}.UploadedFile`} />
      </h6>

      <div className="uploaded-files">
        {!hasFiles && !hasRejections && (
          <div className="no-file-added">
            <FormattedMessageV2 toLocalize={`${i18nBasePath}.NoFileAdded`} />
          </div>
        )}
        {hasFiles && filesWithIds.map(({ file, id }) => <FileDisplay key={id} file={file} i18nBasePath={i18nBasePath} onRemove={onRemove} />)}
        {hasRejections &&
          rejectionsWithIds.map(({ rejection, id }) => (
            <FileDisplay key={id} file={rejection.file} rejectionCode={rejection.errors[0].code} i18nBasePath={i18nBasePath} onRemove={onRemove} />
          ))}
      </div>
    </div>
  );
};

export default UploadStep;
